import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import ApiClient from '../../lib/ApiClient';
import { useLogger } from '@ui/shared/components/Logger';
import { RECORDING_START_CHECK_INTERVAL, LOGGER_LABEL } from './utils/constants';

const MAX_RETRY_ATTEMPTS = 60; // 60 seconds at 1s intervals

// Context for installSid, examSid, sessionSid, and any other shared recording state
export const RecordingContext = createContext({
  installSid: null,
  examSid: null,
  sessionSid: null,
  showTrackSid: false,
  recordingPlaybackReady: false,
  spMediaAuthToken: null,
  apiClient: null,
});

export const useRecordingContext = () => useContext(RecordingContext);

export const RecordingProvider = ({
  installSid,
  examSid,
  sessionSid,
  spMediaAuthToken,
  showTrackSid = false,
  children,
}) => {
  const logger = useLogger();
  
  const apiClient = useMemo(() => {
    if (!spMediaAuthToken) {
      logger.error(LOGGER_LABEL, 'Missing spMediaAuthToken, API calls will fail');
      return null;
    }
    logger.debug(LOGGER_LABEL, 'API client initialized');
    return new ApiClient({ spToken: spMediaAuthToken });
  }, [spMediaAuthToken, logger]);
  
  const [recordingPlaybackReady, setRecordingPlaybackReady] = useState(false);
  const [maxRetriesReached, setMaxRetriesReached] = useState(false);
  const checkRecordingIntervalRef = useRef(null);
  const retryCountRef = useRef(0);
  
  // Log provider initialization
  useEffect(() => {
    logger.info(LOGGER_LABEL, 'Initializing RecordingProvider', {
      installSid,
      examSid,
      sessionSid,
      hasToken: !!spMediaAuthToken,
      showTrackSid
    });
  }, [installSid, examSid, sessionSid, spMediaAuthToken, showTrackSid, logger]);
  
  // Function to check if m3u8 file is available
  const checkRecordingFileAvailability = async () => {
    if (!sessionSid || recordingPlaybackReady) return;
    
    if (!apiClient) {
      logger.error(LOGGER_LABEL, 'Cannot check recording: API client not available');
      return;
    }

    retryCountRef.current++;
    
    try {
      // Endpoint should return information about the m3u8 file availability
      const response = await apiClient.checkRecordingFileAvailability({
        installSid,
        examSid,
        sessionSid,
        spMediaAuthToken,
      });
      if (response.successful && response.data?.startDate) {
        
        logger.info(LOGGER_LABEL, 'Recording file is now available', { 
          startDate: response.data.startDate,
          retriesNeeded: retryCountRef.current
        });
        
        // Signal that recording playback is ready to go...
        setRecordingPlaybackReady(true);
        
        // go ahead and clear the interval, do this before making API calls in case of errors or those calls are slow...
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
        }

        // Add annotation, this will also signal a data channel message...
        try{
          await apiClient.addAnnotation({
            installSid,
            examSid,
            sessionSid,
            payload: {
              typeId: 'recording-file-available',
              createdById: 'Session Recorder',
              metadata: {
                startDate: response.data.startDate,
              },
            },
          });
          logger.info(LOGGER_LABEL, 'Successfully added recording file available annotation');
        }catch(error){
          logger.error(LOGGER_LABEL, 'Failed to add recording file available annotation', {
            error,
            startDate: response.data.startDate
          });
        }
        
        // now update the session recording start date with the actual start date...
        try{
          await apiClient.patchSession({
            installSid,
            examSid,
            sessionSid,
            recordingStartDate: response.data.startDate,
            status: 'Onboarding',
          });
          logger.info(LOGGER_LABEL, 'Successfully updated recording start date');
        }catch(error){
          logger.error(LOGGER_LABEL, 'Failed to update recording start date', {
            error,
            startDate: response.data.startDate
          });
        }
      } else {
        logger.debug(LOGGER_LABEL, 'Recording file not available yet', {
          retryCount: retryCountRef.current,
          willRetryIn: RECORDING_START_CHECK_INTERVAL
        });
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error checking recording file availability', {
        error,
        retryCount: retryCountRef.current,
        installSid,
        examSid,
        sessionSid
      });
    }
  };

  // Poll for m3u8 file availability
  useEffect(() => {
    if (!sessionSid) {
      logger.warn(LOGGER_LABEL, 'Missing sessionSid, cannot check recording availability');
      return;
    }
    
    if (maxRetriesReached) {
      logger.error(LOGGER_LABEL, 'Max retries reached, stopping checks');
      return;
    }

    // Clear any existing interval before creating new one
    if (checkRecordingIntervalRef.current) {
      clearInterval(checkRecordingIntervalRef.current);
      checkRecordingIntervalRef.current = null;
    }

    // Check immediately when component mounts
    checkRecordingFileAvailability();
    
    // If not set, poll every interval
    checkRecordingIntervalRef.current = setInterval(() => {
      if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
        logger.error(LOGGER_LABEL, 'Max retry attempts reached', {
          attempts: retryCountRef.current
        });
        setMaxRetriesReached(true);
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
        }
        return;
      }
      
      if (!recordingPlaybackReady) {
        checkRecordingFileAvailability();
      } else {
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
        }
      }
    }, RECORDING_START_CHECK_INTERVAL);

    // Clean up interval on unmount
    return () => {
      if (checkRecordingIntervalRef.current) {
        clearInterval(checkRecordingIntervalRef.current);
        checkRecordingIntervalRef.current = null;
      }
    };

  }, [sessionSid, recordingPlaybackReady, maxRetriesReached]);

  return (
    <RecordingContext.Provider
      value={{
        installSid,
        examSid,
        sessionSid,
        showTrackSid,
        recordingPlaybackReady,
        spMediaAuthToken,
        apiClient,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
};
